/**
 * PWA field-diagnostic logger. Gated AND noisy on purpose: only fires when
 *   - `import.meta.env.VITE_DEBUG_PWA === "1"` (build-time opt-in), OR
 *   - URL contains `?pwa-debug=1` (runtime opt-in via remote DevTools)
 *
 * Why we need this: Samsung S8 + Chrome WebView 90 in the field. Remote
 * inspect via chrome://inspect can read these logs to confirm whether the
 * manifest parsed, the SW registered, and `beforeinstallprompt` fired.
 *
 * SAFE BY DEFAULT: returns false synchronously when neither gate is on, so
 * production users never see PWA diagnostics in their console.
 */

const DEBUG_QUERY_KEY = "pwa-debug";
const DEBUG_PREFIX = "[pwa]";

export interface PwaDebugRuntime {
  readonly env: { readonly VITE_DEBUG_PWA?: string };
  readonly location: { readonly search: string };
  readonly consoleRef: Pick<Console, "info" | "warn">;
}

export function isPwaDebugOn(
  runtime: PwaDebugRuntime = defaultDebugRuntime(),
): boolean {
  if (runtime.env.VITE_DEBUG_PWA === "1") return true;
  if (typeof runtime.location.search !== "string") return false;
  const params = new URLSearchParams(runtime.location.search);
  return params.get(DEBUG_QUERY_KEY) === "1";
}

export function pwaDebugLog(
  message: string,
  payload?: Record<string, unknown>,
  runtime: PwaDebugRuntime = defaultDebugRuntime(),
): void {
  if (!isPwaDebugOn(runtime)) return;
  runtime.consoleRef.info(`${DEBUG_PREFIX} ${message}`, payload ?? {});
}

export function pwaDebugWarn(
  message: string,
  payload?: Record<string, unknown>,
  runtime: PwaDebugRuntime = defaultDebugRuntime(),
): void {
  if (!isPwaDebugOn(runtime)) return;
  runtime.consoleRef.warn(`${DEBUG_PREFIX} ${message}`, payload ?? {});
}

function defaultDebugRuntime(): PwaDebugRuntime {
  return {
    env: import.meta.env as { VITE_DEBUG_PWA?: string },
    location:
      typeof window === "undefined"
        ? { search: "" }
        : { search: window.location.search },
    consoleRef: console,
  };
}
